import { useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RunStatus, type ThreadMessageDto } from '@aiai/shared';
import { qk } from '../api/queryKeys';
import { fetchActiveThreadRun } from '../api';
import { getToken } from '../lib/token';
import { refreshAccessToken } from '../lib/api';
import { getActiveOrgId } from '../lib/activeOrg';
import type { SocketStatus } from './useIssueSocket';

const SOCKET_BASE = import.meta.env.VITE_API_URL ?? '';

/**
 * Subscribes to the `/threads` Socket.IO namespace for a single project's
 * threads. Invalidates the thread queries (list, detail, messages — all under
 * the `qk.threads` root) on new activity and tracks which thread the PM is
 * currently answering (`thinkingThreadId`).
 *
 * Adapted from `useIssueSocket`, with three thread-specific differences:
 *  - the subscribe ack is checked — a `{ ok: false }` (membership/access denied)
 *    flips `subscribeFailed` so the page can surface a quiet "live updates
 *    unavailable" note instead of silently going stale.
 *  - the thinking state is *seeded* on mount from any in-flight chat-lane run,
 *    so a viewer landing mid-turn sees the indicator before the first event.
 *  - `failedThreadId` exposes the thread whose last turn ended non-succeeded,
 *    so the page can invite a retry. It clears on the next `message`.
 *
 * `thinkingThreadId`/`failedThreadId` may be the sentinel `'*'` when the server
 * did not name a thread (a legacy in-flight run) — treat that as "applies to
 * whichever thread is open".
 *
 * `onPmMessage` is called with every new PM post (used for opt-in desktop
 * notifications); it is kept in a ref so a new callback never resubscribes.
 */
export function useThreadSocket(
  projectId: string | undefined,
  onPmMessage?: (message: ThreadMessageDto) => void,
): {
  thinkingThreadId: string | null;
  status: SocketStatus;
  subscribeFailed: boolean;
  failedThreadId: string | null;
} {
  const [status, setStatus] = useState<SocketStatus>('connecting');
  const [thinkingThreadId, setThinkingThreadId] = useState<string | null>(null);
  const [subscribeFailed, setSubscribeFailed] = useState(false);
  const [failedThreadId, setFailedThreadId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const onPmMessageRef = useRef(onPmMessage);
  onPmMessageRef.current = onPmMessage;

  // Seed the thinking state from an in-flight chat-lane run so a fresh mount
  // (or a page reload mid-turn) shows the indicator immediately.
  const activeRunQuery = useQuery({
    queryKey: [...qk.threads(projectId ?? ''), 'active-run'],
    queryFn: () => fetchActiveThreadRun(projectId!),
    enabled: !!projectId,
  });
  const seededThinking = activeRunQuery.data ? (activeRunQuery.data.threadId ?? '*') : null;

  useEffect(() => {
    if (!projectId) return;

    setSubscribeFailed(false);

    const socket: Socket = io(`${SOCKET_BASE}/threads`, {
      path: '/socket.io',
      transports: ['websocket'],
      auth: { token: getToken() },
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 500,
      reconnectionDelayMax: 8000,
    });

    setStatus('connecting');

    const subscribe = () => {
      setStatus('connected');
      socket.emit('subscribe', { projectId, orgId: getActiveOrgId() }, (ack: { ok: boolean }) => {
        setSubscribeFailed(!ack?.ok);
      });
    };

    socket.on('connect', subscribe);
    socket.io.on('reconnect_attempt', () => setStatus('reconnecting'));
    socket.on('disconnect', (reason: string) => {
      setStatus('disconnected');
      if (reason === 'io server disconnect') {
        setStatus('reconnecting');
        void refreshAccessToken().then((newToken) => {
          if (newToken) {
            socket.auth = { token: newToken };
            socket.connect();
          } else {
            setStatus('disconnected');
          }
        });
      }
    });
    socket.on('connect_error', () =>
      setStatus((s) => (s === 'connected' ? 'reconnecting' : 'disconnected')),
    );

    const invalidate = () => void queryClient.invalidateQueries({ queryKey: qk.threads(projectId) });

    socket.on('message', (payload?: { message?: ThreadMessageDto }) => {
      setFailedThreadId(null);
      if (payload?.message?.role === 'pm') onPmMessageRef.current?.(payload.message);
      invalidate();
    });

    // The PM rewrote the summary and/or the ledger changed: the thread page's
    // rail refetches (detail + list both hang off the root key).
    socket.on('thread-updated', invalidate);

    socket.on('pm-turn-started', (payload: { threadId?: string | null }) => {
      setThinkingThreadId(payload?.threadId ?? '*');
    });

    socket.on('pm-turn-settled', (payload: { status?: RunStatus; threadId?: string | null }) => {
      setThinkingThreadId(null);
      if (payload?.status && payload.status !== RunStatus.Succeeded) {
        setFailedThreadId(payload?.threadId ?? '*');
      }
      invalidate();
    });

    return () => {
      socket.emit('unsubscribe', { projectId });
      socket.removeAllListeners();
      socket.disconnect();
    };
  }, [projectId, queryClient]);

  return {
    thinkingThreadId: thinkingThreadId ?? seededThinking,
    status,
    subscribeFailed,
    failedThreadId,
  };
}
